import { defaultTo, keys } from 'ramda';
import { isNilOrEmpty, isNilOrEmptyString } from 'ramda-extension';

import Bizmachine from '@gef-ui/material-icons/Bizmachine';
import ContactGeneraliFidelityLow from '@gef-ui/material-illustrations-2023/other/ContactGeneraliFidelityLow';
import DownloadDocumentFidelityLow from '@gef-ui/material-illustrations-2023/other/DownloadDocumentFidelityLow';
import FoFidelityHigh from '@gef-ui/material-illustrations-2023/other/FoFidelityHigh';
import HeartFidelityLow from '@gef-ui/material-illustrations-2023/other/HeartFidelityLow';
import ReducedTimeFidelityLow from '@gef-ui/material-illustrations-2023/other/ReducedTimeFidelityLow';
import ParentsFidelityLow from '@gef-ui/material-illustrations-2023/people/ParentsFidelityLow';
import WomanCelebrateFidelityLow from '@gef-ui/material-illustrations-2023/people/WomanCelebrateFidelityLow';
import AccidentPovFidelityLow from '@gef-ui/material-illustrations-2023/povhavcar/AccidentPovFidelityLow';
import Clock2FidelityLow from '@gef-ui/material-illustrations-2023/product/Clock2FidelityLow';
import CustomerRelationshipFidelityLow from '@gef-ui/material-illustrations-2023/product/CustomerRelationshipFidelityLow';
import DogFidelityLow from '@gef-ui/material-illustrations-2023/product/DogFidelityLow';
import HealthCFidelityLow from '@gef-ui/material-illustrations-2023/product/HealthCFidelityLow';
import HouseAssistanceFidelityLow from '@gef-ui/material-illustrations-2023/product/HouseAssistanceFidelityLow';
import HouseInsuranceFidelityLow from '@gef-ui/material-illustrations-2023/product/HouseInsuranceFidelityLow';
import KayakFidelityLow from '@gef-ui/material-illustrations-2023/product/KayakFidelityLow';
import LifeAccidentDiscoverServices2FidelityLow from '@gef-ui/material-illustrations-2023/product/LifeAccidentDiscoverServices2FidelityLow';
import Property2FidelityLow from '@gef-ui/material-illustrations-2023/product/Property2FidelityLow';
import ProtectionDamageFidelityLow from '@gef-ui/material-illustrations-2023/product/ProtectionDamageFidelityLow';
import RideCheck2FidelityLow from '@gef-ui/material-illustrations-2023/product/RideCheck2FidelityLow';
import Team2FidelityLow from '@gef-ui/material-illustrations-2023/product/Team2FidelityLow';
import TransportFidelityLow from '@gef-ui/material-illustrations-2023/product/TransportFidelityLow';
import TravelFidelityLow from '@gef-ui/material-illustrations-2023/product/TravelFidelityLow';
import VehicleFidelityLow from '@gef-ui/material-illustrations-2023/product/VehicleFidelityLow';

import InfoCircleTooltip from 'components/atoms/GefMui/InfoCircleTooltip';
import { EMPTY_VALUES } from 'constants/general';
import { cleanse } from 'utils/object';

import { formDataBeMapper, leadTypesIds } from './constants';

export const requestFactory = (formData) => {
	const body = {};

	keys(formData).forEach((key) => {
		const objKey = formDataBeMapper[key];
		const value = formData[key];
		body[objKey] = value;
	});

	return cleanse(body);
};

export const requestFactoryInProgressAndAfterDeadline = ({ login, taskTypeId }) => {
	const body = {};
	const filter = [
		{
			property: 'queueGroupCd',
			operation: 'equal',
			value: [login],
		},
	];

	if (!isNilOrEmptyString(taskTypeId)) {
		filter.push({
			property: 'taskTypeId',
			operation: 'equal',
			value: [`${taskTypeId}`],
		});
	}

	body.filter = filter;

	return { ...body, showProcessedOrExpired: true };
};

export const chooseIconBasedOnTasksubtypeId = ({ taskSubtypeId, width, height }) => {
	switch (taskSubtypeId) {
		case leadTypesIds.meetingFromSellerWeb:
			return <CustomerRelationshipFidelityLow width={width} height={height} />;
		case leadTypesIds.autoPOV:
			return <VehicleFidelityLow width={width} height={height} />;
		case leadTypesIds.autoHAV:
			return <AccidentPovFidelityLow width={width} height={height} />;
		case leadTypesIds.property:
			return <HouseInsuranceFidelityLow width={width} height={height} />;
		case leadTypesIds.pet:
			return <DogFidelityLow width={width} height={height} />;
		case leadTypesIds.responsibility:
			return <ProtectionDamageFidelityLow width={width} height={height} />;
		case leadTypesIds.traveling:
			return <TravelFidelityLow width={width} height={height} />;
		case leadTypesIds.autoPOVAndHAV:
			return <RideCheck2FidelityLow width={width} height={height} />;
		case leadTypesIds.questionFromMyGenerali:
			return <ContactGeneraliFidelityLow width={width} height={height} />;
		case leadTypesIds.leadProductPageNZP:
			return <HouseAssistanceFidelityLow width={width} height={height} />;
		case leadTypesIds.sportEquipmentInsurance:
		case leadTypesIds.sportEquipmentInsuranceFcb:
			return <KayakFidelityLow width={width} height={height} />;
		case leadTypesIds.fleetInsurance:
			return <TransportFidelityLow width={width} height={height} />;
		case leadTypesIds.apartmentBuilding:
			return <Property2FidelityLow width={width} height={height} />;
		case leadTypesIds.myGeneraliMeeting:
			return <Clock2FidelityLow width={width} height={height} />;
	}
};

export const generateLeadIcon = ({ taskTypeId, taskSubtypeId, width, height }) => {
	// when taskSubTypeId is passed > we are generating icons for competition section
	const isForCompetitionIcon = !isNilOrEmpty(taskSubtypeId);

	switch (taskTypeId) {
		case leadTypesIds.birthday:
			return <WomanCelebrateFidelityLow width={width} height={height} />;
		case leadTypesIds.nameday:
			return <ReducedTimeFidelityLow width={width} height={height} />;
		case leadTypesIds.contractAnniversary5years:
		case leadTypesIds.contractAnniversary10years:
			return <HealthCFidelityLow width={width} height={height} />;
		case leadTypesIds.clientAnniversary3years:
		case leadTypesIds.clientAnniversary5years:
		case leadTypesIds.clientAnniversary10years:
			return <ParentsFidelityLow width={width} height={height} />;
		case leadTypesIds.businessOpportunity:
			return <Team2FidelityLow width={width} height={height} />;
		case leadTypesIds.leadZP:
			return <HeartFidelityLow width={width} height={height} />;
		case leadTypesIds.opportunityLead:
			return <LifeAccidentDiscoverServices2FidelityLow width={width} height={height} />;
		case leadTypesIds.acquisitionsContacts:
			return <FoFidelityHigh width={width} height={height} />;
		case leadTypesIds.businessOpportunitySME:
			return <Bizmachine sx={{ color: 'primary.main' }} width={width} height={height} />;
		case leadTypesIds.webLead:
			return isForCompetitionIcon ? (
				chooseIconBasedOnTasksubtypeId({ taskSubtypeId, width, height })
			) : (
				<DownloadDocumentFidelityLow width={width} height={height} />
			);
	}
};

export const generateInfoCircleTooltip = (lastNote) => {
	let note = defaultTo(EMPTY_VALUES.STRING)(lastNote);
	const tagsToFilterOut = ['<br/>', '<br />', '<br>'];

	tagsToFilterOut.forEach((tag) => {
		const tempNote = note.replaceAll(tag, EMPTY_VALUES.STRING);
		note = tempNote;
	});

	let finalNote;
	if (note.length > 100) {
		// trim string, dont show whole note if longer than 100 chars
		finalNote = note.slice(0, 100).concat('...');
	} else {
		finalNote = note;
	}

	return !isNilOrEmptyString(finalNote) && <InfoCircleTooltip text={finalNote} />;
};

export const generateRemainingHoursColor = ({ totalHours, remainingHours }) => {
	const passedPeriod = totalHours - remainingHours;
	const passedPercentageValue = (passedPeriod / totalHours) * 100;
	const remainingTimePercentage = Math.floor(100 - passedPercentageValue);

	let remainingHoursColor;
	if (remainingTimePercentage >= 50) {
		remainingHoursColor = '#619D9F';
	} else if (remainingTimePercentage <= 49 && remainingTimePercentage >= 25) {
		remainingHoursColor = '#F6A363';
	} else {
		remainingHoursColor = '#EF6752';
	}

	return remainingHoursColor;
};
